import React from 'react';

/**
 * CampaignReferencesPanel
 * =======================
 * Renders thumbnails for every visual reference the user provided to the
 * AI image generator: the product image, the user's avatar/portrait, and
 * the brand logo. Shows only the references that exist (no empty slots).
 *
 * Props:
 *   product      — campaign.product object (must contain product_image,
 *                  avatar_image_url, brand_logo_url fields).
 *   variant      — 'card' (full panel with heading) | 'compact' (chip row)
 *   testIdPrefix — optional, defaults to 'campaign-refs'
 */
const REFERENCE_DEFS = [
  {
    key: 'product_image',
    label: 'Product',
    accent: 'border-blue-300 dark:border-blue-700',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  },
  {
    key: 'avatar_image_url',
    label: 'Your Avatar',
    accent: 'border-purple-300 dark:border-purple-700',
    badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  },
  {
    key: 'brand_logo_url',
    label: 'Brand Logo',
    accent: 'border-emerald-300 dark:border-emerald-700',
    badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  },
];

function resolveAssetUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  // Relative path — join with the backend origin so the browser can fetch it
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
}

function ReferenceThumb({ url, label, accent, badge, variant, testId }) {
  const size = variant === 'compact' ? 'w-14 h-14' : 'w-24 h-24';
  const isLogo = label === 'Brand Logo';
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={testId}>
      <div
        className={`${size} rounded-xl overflow-hidden border-2 ${accent} bg-slate-50 dark:bg-slate-900 shadow-sm`}
      >
        <img
          src={url}
          alt={label}
          className={`w-full h-full ${isLogo ? 'object-contain p-1.5' : 'object-cover'}`}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge}`}>
        {label}
      </span>
    </div>
  );
}

export default function CampaignReferencesPanel({
  product,
  variant = 'card',
  testIdPrefix = 'campaign-refs',
}) {
  if (!product) return null;

  // Collect every product image (new plural field + legacy singular)
  const productImgs = [];
  const seen = new Set();
  if (Array.isArray(product.product_images)) {
    for (const raw of product.product_images) {
      const u = resolveAssetUrl(raw);
      if (u && !seen.has(u)) { productImgs.push(u); seen.add(u); }
    }
  }
  const legacy = resolveAssetUrl(product.product_image);
  if (legacy && !seen.has(legacy)) { productImgs.push(legacy); seen.add(legacy); }

  // Collect every avatar (new plural field + legacy singular). Uses its OWN
  // seen-set so an accidental URL collision with a product image doesn't
  // silently swallow the avatar reference.
  const avatarImgs = [];
  const avSeen = new Set();
  if (Array.isArray(product.avatar_image_urls)) {
    for (const raw of product.avatar_image_urls) {
      const u = resolveAssetUrl(raw);
      if (u && !avSeen.has(u)) { avatarImgs.push(u); avSeen.add(u); }
    }
  }
  const legacyAvatar = resolveAssetUrl(product.avatar_image_url);
  if (legacyAvatar && !avSeen.has(legacyAvatar)) { avatarImgs.push(legacyAvatar); avSeen.add(legacyAvatar); }

  const productDef = REFERENCE_DEFS.find((d) => d.key === 'product_image');
  const avatarDef = REFERENCE_DEFS.find((d) => d.key === 'avatar_image_url');
  const logoDef = REFERENCE_DEFS.find((d) => d.key === 'brand_logo_url');

  const items = [];
  productImgs.forEach((url, idx) => {
    items.push({
      ...productDef,
      key: `product_image_${idx}`,
      url,
      label: productImgs.length > 1 ? `Product ${idx + 1}` : 'Product',
    });
  });
  avatarImgs.forEach((url, idx) => {
    items.push({
      ...avatarDef,
      key: `avatar_${idx}`,
      url,
      label: avatarImgs.length > 1 ? `Avatar ${idx + 1}` : 'Your Avatar',
    });
  });
  const logoUrl = resolveAssetUrl(product.brand_logo_url);
  if (logoUrl) items.push({ ...logoDef, url: logoUrl });

  if (items.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div
        className="flex items-center gap-3 flex-wrap"
        data-testid={`${testIdPrefix}-compact`}
      >
        {items.map((item) => (
          <ReferenceThumb
            key={item.key}
            url={item.url}
            label={item.label}
            accent={item.accent}
            badge={item.badge}
            variant="compact"
            testId={`${testIdPrefix}-${item.key}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-card`}>
      <h3 className="font-semibold text-slate-800 dark:text-white mb-3 text-sm flex items-center gap-2">
        References Used
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
          ({items.length} {items.length === 1 ? 'asset' : 'assets'} sent to AI)
        </span>
      </h3>
      <div className="flex flex-wrap gap-4">
        {items.map((item) => (
          <ReferenceThumb
            key={item.key}
            url={item.url}
            label={item.label}
            accent={item.accent}
            badge={item.badge}
            variant="card"
            testId={`${testIdPrefix}-${item.key}`}
          />
        ))}
      </div>
    </div>
  );
}
